import React, { useState } from 'react';
import { Loader2, CheckCircle2, Send } from 'lucide-react';
import { createSupportEnquiry, ENQUIRY_CATEGORIES } from '@/lib/supportEnquiry';
import { useToast } from '@/components/ui/use-toast';

const EMPTY = {
  full_name: '',
  email: '',
  phone: '',
  enquiry_category: 'general_support',
  order_reference: '',
  message: '',
};

export default function SupportForm() {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = 'Please enter your full name.';
    if (!form.email.trim()) er.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = 'Please enter a valid email address.';
    if (!form.message.trim()) er.message = 'Please enter a message.';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await createSupportEnquiry({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        enquiry_category: form.enquiry_category,
        order_reference: form.order_reference.trim(),
        message: form.message.trim(),
        source: 'support_form',
      });
      setDone(res.reference);
      setForm(EMPTY);
    } catch (err) {
      toast({ title: 'Could not submit your request', description: err?.message || 'Please try again or email the team.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-[#111115] border border-white/5 p-7 text-center">
        <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-3" />
        <p className="text-primary text-[10px] font-bold tracking-widest mb-2">REQUEST LOGGED</p>
        <p className="text-white font-black text-lg mb-1">Your request has been logged</p>
        <p className="text-white/50 text-sm mb-5">
          Ticket <span className="text-white font-bold">#{done}</span> · Our team will reply by email shortly.
        </p>
        <button
          onClick={() => setDone(null)}
          className="inline-flex items-center justify-center gap-2 border border-primary/40 text-primary hover:bg-primary hover:text-white text-xs font-bold tracking-widest px-5 py-2.5 transition-colors"
        >
          SUBMIT ANOTHER REQUEST
        </button>
      </div>
    );
  }

  return (
    <div className="bg-[#111115] border border-white/5 p-7">
      <p className="text-primary text-[10px] font-bold tracking-widest mb-4">SEND A SUPPORT REQUEST</p>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Full Name" required error={errors.full_name}>
          <input
            value={form.full_name}
            onChange={set('full_name')}
            placeholder="Your full name"
            className="seo-input"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Email Address" required error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="you@email.com"
              className="seo-input"
            />
          </Field>
          <Field label="Phone Number">
            <input
              value={form.phone}
              onChange={set('phone')}
              placeholder="Optional"
              className="seo-input"
            />
          </Field>
        </div>
        <Field label="Enquiry Category" required>
          <select value={form.enquiry_category} onChange={set('enquiry_category')} className="seo-input">
            {ENQUIRY_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Order / Booking Reference">
          <input
            value={form.order_reference}
            onChange={set('order_reference')}
            placeholder="Optional"
            className="seo-input"
          />
        </Field>
        <Field label="Message" required error={errors.message}>
          <textarea
            value={form.message}
            onChange={set('message')}
            placeholder="How can we help?"
            rows={5}
            className="seo-input resize-none"
          />
        </Field>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-widest px-6 py-3 disabled:opacity-50 transition-colors"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {submitting ? 'SUBMITTING…' : 'SUBMIT REQUEST'}
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-white/40 text-[10px] font-bold tracking-widest mb-1.5">
        {label.toUpperCase()}{required && <span className="text-primary"> *</span>}
      </label>
      {children}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}